function tokenizeFormula(formula: string) {
  const tokens: Array<{ type: 'num' | 'var' | 'op'; value: number | string }> = [];
  let index = 0;

  while (index < formula.length) {
    const char = formula[index];

    if (/\s/.test(char)) {
      index += 1;
      continue;
    }

    if (/[+\-*/()]/.test(char)) {
      tokens.push({ type: 'op', value: char });
      index += 1;
      continue;
    }

    if (/[0-9.]/.test(char)) {
      let end = index;
      while (end < formula.length && /[0-9.]/.test(formula[end])) {
        end += 1;
      }
      const num = Number(formula.slice(index, end));
      if (!Number.isFinite(num)) {
        throw new Error('Invalid number in formula');
      }
      tokens.push({ type: 'num', value: num });
      index = end;
      continue;
    }

    if (/[a-zA-Z_]/.test(char)) {
      let end = index;
      while (end < formula.length && /[a-zA-Z0-9_]/.test(formula[end])) {
        end += 1;
      }
      const ident = formula.slice(index, end);
      if (ident !== 'level') {
        throw new Error('Only "level" variable is allowed');
      }
      tokens.push({ type: 'var', value: ident });
      index = end;
      continue;
    }

    throw new Error('Invalid character in formula');
  }

  return tokens;
}

function toRpn(tokens: Array<{ type: 'num' | 'var' | 'op'; value: number | string }>) {
  const output: Array<{ type: 'num' | 'var' | 'op'; value: number | string }> = [];
  const operators: Array<{ type: 'op'; value: string }> = [];
  const precedence: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 };
  let prevType: 'start' | 'num' | 'var' | 'op' | 'lparen' | 'rparen' = 'start';

  for (const token of tokens) {
    if (token.type === 'num' || token.type === 'var') {
      output.push(token);
      prevType = token.type;
      continue;
    }

    const op = String(token.value);

    if (op === '(') {
      operators.push({ type: 'op', value: op });
      prevType = 'lparen';
      continue;
    }

    if (op === ')') {
      while (operators.length && operators[operators.length - 1].value !== '(') {
        output.push(operators.pop()!);
      }
      if (!operators.length) {
        throw new Error('Mismatched parentheses');
      }
      operators.pop();
      prevType = 'rparen';
      continue;
    }

    const unaryMinus = op === '-' && (prevType === 'start' || prevType === 'op' || prevType === 'lparen');
    if (unaryMinus) {
      output.push({ type: 'num', value: 0 });
    }

    while (
      operators.length &&
      operators[operators.length - 1].value !== '(' &&
      precedence[operators[operators.length - 1].value] >= precedence[op]
    ) {
      output.push(operators.pop()!);
    }

    operators.push({ type: 'op', value: op });
    prevType = 'op';
  }

  while (operators.length) {
    const op = operators.pop()!;
    if (op.value === '(' || op.value === ')') {
      throw new Error('Mismatched parentheses');
    }
    output.push(op);
  }

  return output;
}

function evalRpn(rpn: Array<{ type: 'num' | 'var' | 'op'; value: number | string }>, level: number) {
  const stack: number[] = [];

  for (const token of rpn) {
    if (token.type === 'num') {
      stack.push(Number(token.value));
      continue;
    }

    if (token.type === 'var') {
      stack.push(level);
      continue;
    }

    const b = stack.pop();
    const a = stack.pop();
    if (a === undefined || b === undefined) {
      throw new Error('Invalid formula expression');
    }

    const op = String(token.value);
    if (op === '+') stack.push(a + b);
    else if (op === '-') stack.push(a - b);
    else if (op === '*') stack.push(a * b);
    else if (op === '/') {
      if (b === 0) throw new Error('Division by zero');
      stack.push(a / b);
    } else {
      throw new Error('Unsupported operator');
    }
  }

  if (stack.length !== 1 || !Number.isFinite(stack[0])) {
    throw new Error('Invalid formula result');
  }

  return stack[0];
}

export function evaluateSafeFormula(formula: string | number | null | undefined, level: number): number {
  if (formula === null || formula === undefined) return 0;

  const numeric = Number(formula);
  if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
    return numeric;
  }

  const safeLevel = Number(level);
  if (!Number.isFinite(safeLevel)) return 0;

  const normalized = String(formula).trim();
  if (!normalized) return 0;

  const tokens = tokenizeFormula(normalized);
  const rpn = toRpn(tokens);
  return evalRpn(rpn, safeLevel);
}
